import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PageService } from "../services/pageService";
import { PageManagement } from "../modules/shared/model/page-management.interfaces";
import { Card } from "./ui/card";
import { Button } from "./ui/Button";
import { Badge } from "./ui/badge";
import { Globe, ExternalLink, Eye } from "lucide-react";

const PageNavigation: React.FC = () => {
  const [pages, setPages] = useState<PageManagement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublishedPages();
  }, []);

  const fetchPublishedPages = async () => {
    try {
      setLoading(true);
      const publishedPages = await PageService.getPublishedPages();
      setPages(publishedPages);
    } catch (error) {
      console.error("Error fetching pages:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="p-6 text-center">
        <Globe className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Published Pages</h3>
        <p className="text-gray-600">There are no published pages available at the moment.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Published Pages</h2>
        <p className="text-gray-600">Browse and access all published pages</p>
      </div>

      <div className="grid gap-4">
        {pages.map((page) => (
          <Card key={page.id} className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{page.title}</h3>
                  <Badge variant="outline" className="bg-green-100 text-green-700">
                    {page.status?.name || "Published"}
                  </Badge>
                  <Badge variant="outline" className="bg-blue-100 text-blue-700">
                    {page.type?.name || "Default Layout"}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                  <div className="flex items-center gap-1">
                    <Globe className="h-4 w-4" />
                    <span>{page.url}</span>
                  </div>
                </div>

                {page.metaDescription && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {page.metaDescription}
                  </p>
                )}

                {page.metaKeywords && page.metaKeywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {page.metaKeywords.slice(0, 3).map((keyword, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
                      >
                        {keyword}
                      </span>
                    ))}
                    {page.metaKeywords.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-500">
                        +{page.metaKeywords.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 ml-4">
                <Link to={page.url || "/"}>
                  <Button variant="outline" size="sm">
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`https://kokobeo.com${page.url}`, "_blank")}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Open
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PageNavigation;
